import { html, nothing } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';
import { classMap } from 'lit/directives/class-map.js';
import type { weight } from '../../../mixins/Weight.js';
import { FULL_CIRCLE_IN_RADIANS } from '../../../visualizations/common/constants.js';
import { ArcSegments } from './ArcSegments.js';
import { ArcPointer } from './ArcPointer.js';
import { DistributionLegend } from '../../../visualizations/common/fragments/DistributionLegend.js';
import { indicationData } from '../types.js';
import { distributionMetadata, distributionValues } from '../../../visualizations/common/types.js';
import type { OneUxIndicationElement } from '../OneUxIndicationElement.js';
const weightStyles: Record<weight, {
  svgWidth: number;
  innerSlotOffset: number;
}> = {
  high: {
    svgWidth: 128,
    innerSlotOffset: 14
  },
  normal: {
    svgWidth: 64,
    innerSlotOffset: 8
  },
  low: {
    svgWidth: 32,
    innerSlotOffset: 4
  }
};
const availableRadiansForSegments = 0.75 * FULL_CIRCLE_IN_RADIANS;
const startAngleForSegments = 0.25 * FULL_CIRCLE_IN_RADIANS + availableRadiansForSegments / 2;
export function ShapeArc(this: OneUxIndicationElement, {
  indication,
  metadata,
  distribution
}: {
  indication: indicationData[];
  metadata: distributionMetadata[];
  distribution: distributionValues;
}) {
  const hasSummary = this._slots.hasNamedSlot('summary');
  const hasDescription = this._slots.hasNamedSlot('description');
  const aspectRatio = 1 / (0.5 * (1 - Math.sin(startAngleForSegments)));
  const {
    innerSlotOffset
  } = weightStyles[this.weight];
  const minSvgWidth = weightStyles[this.weight].svgWidth;
  const minSvgHeight = Math.ceil(minSvgWidth * (1 / aspectRatio));
  let svgWidth = minSvgWidth;
  let svgHeight = minSvgHeight;
  if (this.weight === 'high') {
    const $svg = this.shadowRoot!.querySelector('svg');
    if ($svg) {
      svgWidth = $svg.clientWidth;
      svgHeight = $svg.clientHeight;
    }
  }
  const showLegend = this.weight === 'high' && !this.compact && !this.implicit && !this.static;
  return html`
    <div
      class="arcShape"
      style=${styleMap({
    '--one-ux-indication--arc-aspect-ratio': this.weight === 'high' ? aspectRatio : 'auto'
  })}
    >
      <div
        class="svgAndSlots"
        style=${styleMap({
    width: this.weight === 'high' ? '100%' : 'auto'
  })}
      >
        <svg
          style=${styleMap({
    'min-width': minSvgWidth + 'px',
    'min-height': minSvgHeight + 'px',
    width: this.weight === 'high' ? 'auto' : svgWidth + 'px',
    height: this.weight === 'high' ? 'auto' : svgHeight + 'px'
  })}
          role="presentation"
        >
          ${ArcSegments.call(this, {
    indication,
    size: svgWidth,
    x: 0,
    y: 0,
    startAngle: startAngleForSegments,
    availableRadians: availableRadiansForSegments
  })}
          <foreignObject
            x="${innerSlotOffset}"
            y="${innerSlotOffset}"
            width="${svgWidth - 2 * innerSlotOffset}"
            height=${svgWidth - 2 * innerSlotOffset}
          >
            <div class="innerSlots">
              ${this.weight === 'high' || this.weight === 'normal' ? html`
                    <slot
                      name="summary"
                      class=${classMap({
    summary: true,
    'one-ux-typography--heading-500': this.weight === 'high',
    'one-ux-typography--heading-100': this.weight === 'normal'
  })}
                    ></slot>
                  ` : nothing}
              ${this.weight === 'high' ? html`<slot name="description" class="description one-ux-typography--label-400"></slot>` : nothing}
            </div>
          </foreignObject>
          ${this._isNonZeroTotal ? ArcPointer({
    value: this.value,
    size: svgWidth,
    x: 0,
    y: 0,
    startAngle: startAngleForSegments,
    availableRadians: availableRadiansForSegments,
    weight: this.weight
  }) : null}
        </svg>

        ${this.weight === 'normal' || this.weight == 'low' ? html`
              <div
                class="outerSlots"
                style=${styleMap({
    display: !hasSummary && !hasDescription ? 'none' : null
  })}
              >
                ${this.weight === 'low' ? html`<slot name="summary" class="summary one-ux-typography--heading-100"></slot>` : nothing}

                <slot
                  name="description"
                  class=${classMap({
    description: true,
    'one-ux-typography--label-300': this.weight === 'normal',
    'one-ux-typography--label-100': this.weight === 'low'
  })}
                ></slot>
              </div>
            ` : nothing}
      </div>

      ${showLegend ? DistributionLegend({
    metadata,
    values: distribution
  }) : nothing}
    </div>
  `;
}